/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
/** @jsx jsx */
import { jsx, Styled } from 'theme-ui';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import EmbeddedAsset from './embedded-asset';
import EntryHyperlink from './entry-hyperlink';
import AssetHyperlink from './asset-hyperlink';
import Link from '../components/link';
import convertJsonToPropsInLocale from './convert-json-to-props-in-locale';
import getEmbeddedEntryComponent from './embedded-entry-map';
import { getContentfulPageWithPath } from './page-lookup';
import { getContentfulGatsbyImage } from './image-lookup';
import transformProps from './prop-transformer';

const defaultLocale = 'en-US';

const ContentfulRichTextRenderer = (json) => {
  // console.log(json);
  const Bold = ({ children }) => <span className="bold">{children}</span>;

  const transformContentType = ({ id, type, contentType }, rawjson) => {
    if (type === 'Asset') {
      return getContentfulGatsbyImage(id);
    }
    if (type === 'Entry') {
      switch (contentType) {
        case 'page':
          return { pagePaths: getContentfulPageWithPath(id), contentfulId: id };
        default: {
          // console.log(`convert ${contentType} with id ${id}`, { type, contentType, json });
          // if there's a nested entry, convert them to children for the parent to display
          const props = convertJsonToPropsInLocale(rawjson, defaultLocale, transformContentType);
          const documents = Object.values(props).filter((val) => val.nodeType === 'document');
          const children = documents.map((doc) => ContentfulRichTextRenderer(doc));
          return { children };
        }
      }
    }
    return null;
  };

  const EmbeddedEntry = ({ target }) => {
    const { sys: { contentType } } = target;
    // console.log('target', target)
    if (contentType) {
      const { sys: { id } } = contentType;
      // console.log('contentful block id', id);
      // console.log(`contentful block fields for ${id}`, target.fields);
      const props = convertJsonToPropsInLocale(target.fields, defaultLocale, transformContentType);
      // console.log(`props for ${id}`, props);
      // console.log(`transformed props for ${id}`, transformProps(props));
      const Component = getEmbeddedEntryComponent(id, props);
      if (Component) {
        // compile all props of type rich text and pass them as children
        const documents = Object.values(props).filter((val) => val.nodeType === 'document');
        const children = documents.map((doc) => ContentfulRichTextRenderer(doc));
        return (
          <Component {...transformProps(props, id)}>
            {children}
          </Component>
        );
      }
    }
    return null;
  };

  const contentfulOptions = {
    renderMark: {
      [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => <Styled.h1>{children}</Styled.h1>,
      [BLOCKS.HEADING_2]: (node, children) => <Styled.h2>{children}</Styled.h2>,
      [BLOCKS.HEADING_3]: (node, children) => <Styled.h3>{children}</Styled.h3>,
      [BLOCKS.PARAGRAPH]: (node, children) => {
        // remove blank paragraphs
        if (Array.isArray(children) && children.length === 1 && !children[0]) return null;
        return <Styled.p>{children}</Styled.p>;
      },
      [BLOCKS.EMBEDDED_ASSET]: ({ data }) => <EmbeddedAsset data={data} />,
      [BLOCKS.EMBEDDED_ENTRY]: ({ data }) => <EmbeddedEntry target={data.target} />,
      [INLINES.HYPERLINK]: ({ data }, children) => (
        <Link to={data.uri}>{children}</Link>
      ),
      [INLINES.ASSET_HYPERLINK]: ({ data }, children) => (
        <AssetHyperlink target={data.target}>{children}</AssetHyperlink>
      ),
      [INLINES.ENTRY_HYPERLINK]: ({ data }, children) => (
        <EntryHyperlink target={data.target}>{children}</EntryHyperlink>
      ),
    },
  };

  return documentToReactComponents(json, contentfulOptions);
};

export default ContentfulRichTextRenderer;
